/**
 * Notes and rests drawn rather than typed.
 *
 * The Unicode musical symbols are not in any font this app loads, so they come out as empty
 * boxes; a few lines of SVG say the same thing and are legible everywhere. They live here
 * rather than in the palette that uses them because a button, a menu and a status line can
 * all want to show a crotchet.
 */

/** A note head with as many flags as the value has. `flags` of -1 is a stemless semibreve. */
export function NoteGlyph({ flags, filled }: { flags: number; filled: boolean }): JSX.Element {
  return (
    <svg viewBox="0 0 20 20" className="mx-auto h-5 w-5" aria-hidden="true">
      <ellipse
        cx="7"
        cy="14"
        rx="3.6"
        ry="2.6"
        transform="rotate(-22 7 14)"
        fill={filled ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth="1.2"
      />
      {flags < 0 ? null : (
        <path d="M10.4 13.6V3.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      )}
      {[...Array(Math.max(flags, 0)).keys()].map((index) => (
        <path
          key={index}
          d={`M10.4 ${3.8 + index * 3.2}c3 1.3 4 3 3.2 5`}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
        />
      ))}
    </svg>
  );
}

/** Anything else that has to be drawn: a rest, a tie, a hairpin. */
export function Glyph({ d }: { d: string }): JSX.Element {
  return (
    <svg viewBox="0 0 20 20" className="mx-auto h-5 w-5" aria-hidden="true">
      <path
        d={d}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export const REST_PATH = "M4 9h12M6 9v3h8V9";
/** A mordent: the zigzag, with the stroke through it that inverts it. */
export const MORDENT_PATH = "M2 11l3-3 3 3 3-3 3 3 3-3";
export const INVERTED_MORDENT_PATH = "M2 11l3-3 3 3 3-3 3 3 3-3M10 5v10";
/** A turn: the sideways S, and the same with the stroke that inverts it. */
export const TURN_PATH = "M3 12c0-3 3-3 3 0s3 3 4 0 3-3 3 0 3 3 4 0";
export const INVERTED_TURN_PATH = "M3 12c0-3 3-3 3 0s3 3 4 0 3-3 3 0 3 3 4 0M10 4v12";
/** A fermata: the arc, with the dot beneath it. */
export const FERMATA_PATH = "M3 13c0-6 14-6 14 0M10 11.6v.2";
/** An arpeggio: the vertical wiggle drawn beside the chord it spreads. */
export const ARPEGGIO_PATH = "M10 3c-3 2 3 3 0 5s3 3 0 5s3 3 0 4";
/** A tremolo: the beams struck through a stem. */
export const TREMOLO_PATH = "M5 13l10-5M5 10l10-5M5 16l10-5";
export const TIE_PATH = "M4 8c2.5 4 9.5 4 12 0";
/** A grace note: a small quaver, with the slash that says it is played before the beat. */
export const GRACE_PATH = "M7 15V5M7 5c2 1 3 2.5 3 4.5M4 15h3M3.5 12.5l7-5";
export const SLUR_PATH = "M3 13c3-6 11-6 14 0";
export const CRESCENDO_PATH = "M3 6l14 4-14 4";
export const DIMINUENDO_PATH = "M17 6L3 10l14 4";
/** A pedal line: the bracket held under the staff, with a tick up at each end. */
export const PEDAL_PATH = "M4 7v7h12V7";

/**
 * A glissando: the wavy line, slanting as it does between two notes.
 *
 * The wave is drawn flat and then turned, rather than written as a slanting path, so that
 * its teeth stay the same size as the mordent's instead of being stretched by the slope.
 */
export function GlissandoGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 20 20" className="mx-auto h-5 w-5" aria-hidden="true">
      <path
        d="M1 10l2.5-2.5L6 10l2.5-2.5L11 10l2.5-2.5L16 10"
        transform="rotate(-40 10 10)"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
